/* eslint-disable max-lines, import/max-dependencies */
import React from 'react';

import {email} from '@nfq/safe-validation-regex';
import dynamic from 'next/dynamic';

import {parseAnswersToObject} from 'Frontend/utils/helpers';
import {PRODUCT_CATEGORY} from 'Server/utils/constants';

const ColorQuestion = dynamic(() => import(
    /* webpackChunkName: "colorQuestion" */
    'Frontend/components/interview/questions/ColorQuestion'
));
const ConditionQuestion = dynamic(() => import(
    /* webpackChunkName: "conditionQuestion" */
    'Frontend/components/interview/questions/ConditionQuestion'
));
const DataQuestion = dynamic(() => import(
    /* webpackChunkName: "dataQuestion" */
    'Frontend/components/interview/questions/DataQuestion'
));
const DistanceQuestion = dynamic(() => import(
    /* webpackChunkName: "distanceQuestion" */
    'Frontend/components/interview/questions/DistanceQuestion'
));
const ExperienceQuestion = dynamic(() => import(
    /* webpackChunkName: "experienceQuestion" */
    'Frontend/components/interview/questions/ExperienceQuestion'
));
const GenderQuestion = dynamic(() => import(
    /* webpackChunkName: "genderQuestion" */
    'Frontend/components/interview/questions/GenderQuestion'
));
const HairLengthQuestion = dynamic(() => import(
    /* webpackChunkName: "hairLengthQuestion" */
    'Frontend/components/interview/questions/HairLengthQuestion'
));
const HowBadQuestion = dynamic(() => import(
    /* webpackChunkName: "howBadQuestion" */
    'Frontend/components/interview/questions/HowBadQuestion'
));
const InquiryForQuestion = dynamic(() => import(
    /* webpackChunkName: "inquiryForQuestion" */
    'Frontend/components/interview/questions/InquiryForQuestion'
));
const InterviewIntro = dynamic(() => import(
    /* webpackChunkName: "interviewIntro" */
    'Frontend/components/interview/questions/InterviewIntro'
));
const PriceRangeQuestion = dynamic(() => import(
    /* webpackChunkName: "priceRangeQuestion" */
    'Frontend/components/interview/questions/PriceRangeQuestion'
));
const ProductQuestion = dynamic(() => import(
    /* webpackChunkName: "productQuestion" */
    'Frontend/components/interview/questions/ProductQuestion'
));
const ReasonQuestion = dynamic(() => import(
    /* webpackChunkName: "reasonQuestion" */
    'Frontend/components/interview/questions/ReasonQuestion'
));
const ServiceProductQuestion = dynamic(() => import(
    /* webpackChunkName: "serviceProductQuestion" */
    'Frontend/components/interview/questions/ServiceProductQuestion'
));
const ShowDataQuestion = dynamic(() => import(
    /* webpackChunkName: "showDataQuestion" */
    'Frontend/components/interview/questions/ShowDataQuestion'
));
const SituationQuestion = dynamic(() => import(
    /* webpackChunkName: "situationQuestion" */
    'Frontend/components/interview/questions/SituationQuestion'
));
const StylingQuestion = dynamic(() => import(
    /* webpackChunkName: "stylingQuestion" */
    'Frontend/components/interview/questions/StylingQuestion'
));
const UploadQuestion = dynamic(() => import(
    /* webpackChunkName: "uploadQuestion" */
    'Frontend/components/interview/questions/UploadQuestion'
));
const UrgancyQuestion = dynamic(() => import(
    /* webpackChunkName: "urgancyQuestion" */
    'Frontend/components/interview/questions/UrgancyQuestion'
));
const UsageQuestion = dynamic(() => import(
    /* webpackChunkName: "usageQuestion" */
    'Frontend/components/interview/questions/UsageQuestion'
));

/**
 * Checks which Survay category is the right one.
 *
 * @param {Object} answers An object of answers given.
 *
 * @returns {String} The category.
 */
const checkCategory = answers => (answers.service ? 'Service' : 'Product');

export const Start = {
    // eslint-disable-next-line react/jsx-props-no-spreading
    component: props => <InterviewIntro {...props} />,
    hasNoAnswer: true,
    hasSteps: false,
    infoText: null,
    isNoQuestion: true,
    isSkippable: false,
    key: 'Start',
    question: null,
    subText: null,
    tracking: {
        SurveyStep: '0',
        SurveyStepName: 'Hallo!'
    }
};

export const Products = {
    // eslint-disable-next-line react/jsx-props-no-spreading
    component: props => <ProductQuestion {...props} />,
    hasNoAnswer: false,
    hasSteps: false,
    infoText: null,
    isNoQuestion: false,
    isSkippable: false,
    key: 'Products',
    question: 'Wonach suchst du?',
    questionShort: 'Wonach suchst du',
    subText: null,
    tracking: {
        SurveyStep: '1',
        SurveyStepName: 'Wonach suchst du?'
    }
};

/**
 * The for whom question definition.
 *
 * @param {Object} data     Filled data for now.
 * @param {Number} depth    Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const InquiryFor = (data, depth, maxSteps) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        hairpiece: 'Für wen suchst du ein Haarteil?',
        other: 'Für wen suchst du die Zweithaarlösung?',
        toupee: 'Für wen suchst du ein Toupet?',
        wig: 'Für wen suchst du eine Perücke?'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <InquiryForQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: false,
        key: 'InquiryFor',
        maxSteps,
        question: questions[answers.product],
        questionShort: 'Für Wen Suchst Du',
        step: depth - 1,
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${depth - 1}`,
            SurveyStepName: 'Für wen suchst du'
        }
    };
};

/**
 * The for whom question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Gender = (data, depth, maxSteps) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: 'Welchem Geschlecht ordnest du dich zu?',
        otherPerson: 'Welchem Geschlecht ordnet sich diese Person zu?'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <GenderQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: false,
        key: 'Gender',
        maxSteps,
        question: questions[answers.inquiryIsFor] || 'Welchem Geschlecht ordnest du dich zu?',
        questionShort: 'Welches Geschlecht',
        step: depth - 1,
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${depth - 1}`,
            SurveyStepName: 'Geschlecht'
        }
    };
};

/**
 * The for ServiceProducts question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const ServiceProducts = (data, depth) => {
    const answers = parseAnswersToObject(data);

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ServiceProductQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: false,
        key: 'ServiceProducts',
        question: 'Wofür?',
        questionShort: 'Wofür?',
        step: depth - 1,
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${depth - 1}`,
            SurveyStepName: 'Wofür'
        }
    };
};

/**
 * The for whom question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Situation = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: `
            Möchtest du deine aktuelle Situation und deine Erfahrungen schildern, sodass dir die Zweithaarstudios
            die bestmögliche Beratung bieten können?
        `,
        otherPerson: `
            Möchtest du die aktuelle Situation und Erfahrungen der betroffenen Person schildern, sodass die
            Zweihaarstudios die bestmögliche Beratung anbieten können?
        `
    };
    const info = {
        myself: 'Du kannst Fragen überspringen, bei denen du dir noch nicht sicher bist oder nicht antworten willst.',
        otherPerson: 'Du hast die Möglichkeit Fragen zu überspringen, wenn du dir nicht sicher bist'
    };
    const subtext = {
        myself: null,
        otherPerson: 'Bitte beantworte die folgenden Fragen so gut es geht aus der Sicht des Betroffenen.'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <SituationQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: info[answers.inquiryIsFor],
        isNoQuestion: false,
        isSkippable: false,
        key: 'Situation',
        question: questions[answers.inquiryIsFor],
        questionShort: 'Situation schildern',
        step: depth - 1,
        subText: subtext[answers.inquiryIsFor],
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${depth - 1}`,
            SurveyStepName: 'Situation schildern'
        }
    };
};

/**
 * The for Reason question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const Reason = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: 'Was ist die Ursache deines Haarausfalls?',
        otherPerson: 'Was ist die Ursache des Haarausfalls der Person?'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ReasonQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Reason',
        question: questions[answers.inquiryIsFor],
        questionShort: 'Ursache Haarausfall',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Ursache Haarausfall'
        }
    };
};

/**
 * The for HowBad question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const HowBad = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const question = 'Wie ausgeprägt ist der Haarausfall?';
    const subText = `
        Im nächsten Schritt hast du die Möglichkeit,
        Fotos vom aktuellen Stand anzuhängen,
        falls du das möchtest.
    `;

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <HowBadQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'HowBad',
        question,
        questionShort: 'Wie ausgeprägt',
        step: (depth - 1) + calcSkip(answers),
        subText,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Wie ausgeprägt'
        },
        validation(value) {
            const {hairlossIntensity} = value;

            return Boolean(hairlossIntensity);
        }
    };
};

/**
 * The for Upload question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const Upload = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const question = 'Bilder Upload';
    const subText = `
        Hier hast du die Möglichkeit, Fotos von der aktuellen Situation anzuhängen,
        sodass sich die Zweithaarsalons noch besser vorbereiten können.
    `;

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <UploadQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Upload',
        question,
        step: (depth - 1) + calcSkip(answers),
        subText,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Upload'
        }
    };
};

/**
 * The for experience question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const Experience = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: 'Welche Zweithaarlösung(en) hattest du bereits?',
        otherPerson: 'Welche Zweithaarlösung(en) hatte die Person?'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ExperienceQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Experience',
        question: questions[answers.inquiryIsFor],
        questionShort: 'Was bisher',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Welche Zweithaarlösung bisher'
        }
    };
};

/**
 * The for whom question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Condition = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: `
            Erzähl uns etwas über deine Anforderungen an deinen Haarersatz.
            So können wir genau das passende Studio für deine Bedürfnisse finden
        `,
        otherPerson: `
            Erzähle uns etwas über die Anforderungen an den Haarersatz.
            So können wir genau das passende Studio finden.
        `
    };
    const subtexts = {
        myself: `
            Das hilft uns bei der Wahl des richtigen Zweithaar-Studios und zur Vorbereitung auf deinen Termin.
        `,
        otherPerson: `
            Das hilft uns bei der Wahl des richtigen Zweithaar-Studios und zur Vorbereitung auf den Termin.
        `
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ConditionQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: false,
        key: 'Condition',
        question: questions[answers.inquiryIsFor],
        questionShort: 'Erzähl',
        step: (depth - 1) + calcSkip(answers),
        subText: subtexts[answers.inquiryIsFor],
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Anforderungen'
        }
    };
};

/**
 * The for Styling question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Styling = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: {
            hairpiece: 'Ist es dir wichtig, dein Haarteil selber stylen zu können?',
            other: 'Ist es dir wichtig, das Styling deiner Zweithaarlösung selbst ändern zu können?',
            toupee: 'Ist es dir wichtig, das Toupet selber stylen zu können?',
            wig: 'Ist es dir wichtig, deine Perücke selber stylen zu können?'
        },
        otherPerson: {
            hairpiece: 'Ist es wichtig, das Haarteil selber stylen zu können?',
            other: 'Ist es wichtig, das Styling der Zweithaarlösung selbst ändern zu können?',
            toupee: 'Ist es wichtig, das Toupet selber stylen zu können?',
            wig: 'Ist es wichtig, die Perücke selber stylen zu können?'
        }
    };
    const subtext = '(z. B. glatte/lockige Haare, Scheitel ändern, etc.)';

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <StylingQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Styling',
        question: questions[answers.inquiryIsFor][answers.product],
        questionShort: 'Styling',
        step: (depth - 1) + calcSkip(answers),
        subText: subtext,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Styling'
        }
    };
};

/**
 * The for Color question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Color = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: 'Welche Haarfarbe bevorzugst du?',
        otherPerson: 'Welche Haarfarbe wird bevorzugt?'
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ColorQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Color',
        question: questions[answers.inquiryIsFor],
        questionShort: 'Welche Haarfarbe',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Haarfarbe'
        }
    };
};

/**
 * The for HairLength question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const HairLength = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const question = 'Wie lang sollen die Haare sein?';

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <HairLengthQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'HairLength',
        question,
        questionShort: 'Wie lang',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Haarlänge'
        }
    };
};

/**
 * The for HairLength question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Usage = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: {
            hairpiece: 'Wie oft hast du vor, das Haarteil zu tragen?',
            other: 'Wie oft hast du vor, die Zweithaarlösung zu tragen?',
            wig: 'Wie oft hast du vor, deine Perücke zu tragen?'
        },
        otherPerson: {
            hairpiece: 'Wie oft soll das Haarteil getragen werden?',
            other: 'Wie oft soll die Zweithaarlösung getragen werden?',
            wig: 'Wie oft soll die Perücke getragen werden?'
        }
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <UsageQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Usage',
        question: questions[answers.inquiryIsFor][answers.product],
        questionShort: 'Wie oft tragen',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Wie oft vor zu tragen'
        }
    };
};

/**
 * The for Urgency question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const Urgency = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: {
            hairpiece: 'Bis wann benötigst du das Haarteil?',
            other: 'Bis wann benötigst du die Zweithaarlösung?',
            toupee: 'Bis wann benötigst du das Toupet?',
            wig: 'Bis wann benötigst du die Perücke?'
        },
        otherPerson: {
            hairpiece: 'Bis wann wird das Haarteil benötigt?',
            other: 'Bis wann wird die Zweithaarlösung benötigt?',
            toupee: 'Bis wann wird das Toupet benötigt?',
            wig: 'Bis wann wird die Perücke benötigt?'
        }
    };

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <UrgancyQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText: null,
        isNoQuestion: false,
        isSkippable: true,
        key: 'Urgency',
        question: questions[answers.inquiryIsFor][answers.product],
        questionShort: 'Bis Wann',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Bis wann benötigt'
        }
    };
};

/**
 * The for PriceRange question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 * @param {Number} maxSteps Max number steps.
 *
 * @returns {Object} The question definition.
 */
export const PriceRange = (data, depth) => {
    const answers = parseAnswersToObject(data);
    const questions = {
        myself: {
            hairpiece: 'Wie viel möchtest du maximal ausgeben?',
            other: 'Wie viel möchtest du maximal ausgeben?',
            toupee: 'Wie viel möchtest du maximal ausgeben?',
            wig: 'Wie viel möchtest du maximal ausgeben?'
        },
        otherPerson: {
            hairpiece: 'Wie viel soll das Haarteil maximal kosten?',
            other: 'Wie viel soll die Zweithaarlösung maximal kosten?',
            toupee: 'Wie viel soll das Toupet maximal kosten?',
            wig: 'Wie viel soll die Perücke maximal kosten?'
        }
    };
    const infoText = `Solltest du über ein Rezept für Zweithaar verfügen,
        erhältst du von deiner gesetzl. Krankenkasse eine Zuzahlung. Manche
        Krankenkassen bezuschussen auch Echthaar. Wir empfehlen dir,
        dich direkt bei deiner Krankenkasse zu informieren.`;

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <PriceRangeQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: true,
        infoText,
        isNoQuestion: false,
        isSkippable: true,
        key: 'PriceRange',
        question: questions[answers.inquiryIsFor][answers.product],
        questionShort: 'Wie viel ausgeben',
        step: (depth - 1) + calcSkip(answers),
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Maximale Ausgaben'
        }
    };
};

/**
 * The for Distance question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const Distance = (data, depth) => {
    const answers = parseAnswersToObject(data);

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <DistanceQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: false,
        infoText: null,
        isNoQuestion: true,
        isSkippable: false,
        key: 'Distance',
        questionShort: ['Wo suchst du', 'Umkreis'],
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Umkreis'
        },
        // eslint-disable-next-line no-shadow
        validation(data) {
            if (!data.searchRangeCenter) {
                return false;
            }
            if (!data.searchRangeKm) {
                return false;
            }
            if (data.hasStoresInRange !== true) {
                return false;
            }

            return true;
        }
    };
};

/**
 * Validate Data Questions
 * @param {Object} data Data to validate
 * @returns {Object} object with erros
 */
const validateInterview = data => {
    const errors = {};

    if (!data.firstname) {
        errors.firstname = 'Vorname wird benötigt';
    }

    if (!data.lastname) {
        errors.lastname = 'Nachname wird benötigt';
    }

    if (!data.dateOfBirth) {
        errors.dateOfBirth = 'Geburtsdatum wird benötigt';
    }

    if (data.email && email.test(data.email) === false) {
        errors.email = 'Bitte gültige E-Mail angeben';
    }

    if (!data.email) {
        errors.email = 'E-Mail wird benötigt';
    }

    if (!data.phone) {
        errors.phone = 'Mobilfunknummer wird benötigt';
    }

    if (!data.optin) {
        errors.optin = 'Zustimmung wird benötigt';
    }

    return errors;
};

/**
 * Validate Data Questions
 * @param {Object} data Data to validate
 * @returns {Object} object with erros
 */
export const validateQuickInterview = data => {
    const errors = {};

    if (!data.firstname) {
        errors.firstname = 'Vorname wird benötigt';
    }

    if (!data.lastname) {
        errors.lastname = 'Nachname wird benötigt';
    }

    if (data.email && email.test(data.email) === false) {
        errors.email = 'Bitte gültige E-Mail angeben';
    }

    if (!data.email) {
        errors.email = 'E-Mail wird benötigt';
    }

    if (!data.phone) {
        errors.phone = 'Mobilfunknummer wird benötigt';
    }

    if (!data.optin) {
        errors.optin = 'Zustimmung wird benötigt';
    }

    return errors;
};

/**
 * The for Data question definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const Data = (data, depth) => {
    const answers = parseAnswersToObject(data);

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
        component: props => <DataQuestion {...props} />,
        hasNoAnswer: false,
        hasSteps: false,
        infoText: null,
        isNoQuestion: true,
        isSkippable: false,
        key: 'Data',
        questionShort: [
            'Vorname',
            'Nachname',
            'Geburtsdatum',
            'E-Mail',
            'Mobilfunknummer',
            'Bevorzugter Kontakt',
            'Kontakt Von',
            'Kontakt Bis',
            'Kontakt Checkbox'
        ],
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Persönliche Daten'
        },
        // eslint-disable-next-line no-shadow
        interactionValidation(data) {
            return validateInterview(data);
        },
        // eslint-disable-next-line no-shadow
        validation(data) {
            return Object.values(validateInterview(data)).length === 0;
        }
    };
};

/**
 * The for Show data definition.
 *
 * @param {Object} data  Filled data for now.
 * @param {Number} depth Decision depth.
 *
 * @returns {Object} The question definition.
 */
export const ShowData = (data, depth) => {
    const answers = parseAnswersToObject(data);

    return {
        // eslint-disable-next-line react/jsx-props-no-spreading
        component: props => <ShowDataQuestion {...props} />,
        hasNoAnswer: true,
        hasSteps: false,
        infoText: null,
        isEnd: true,
        isNoQuestion: true,
        isSkippable: false,
        key: 'ShowData',
        subText: null,
        tracking: {
            ProductSelection: PRODUCT_CATEGORY[answers.product],
            SurveyCategory: checkCategory(answers),
            SurveyStep: `${(depth - 1) + calcSkip(answers)}`,
            SurveyStepName: 'Übersicht'
        }
    };
};

/**
 * Calculates the skipped steps with given answers.
 *
 * @param {Object} answers The given answers.
 *
 * @returns {Number} The number of skipped steps.
 */
const calcSkip = answers => {
    let skip = 0;

    /* eslint-disable @nfq/no-magic-numbers */
    if (answers?.product === 'wig') {
        skip += (answers?.situation === 'no') ? 2 : 0;
        skip += (answers?.condition === 'no') ? 3 : 0;
    } else if (answers?.product === 'hairpiece' || answers?.product === 'other') {
        skip += (answers?.situation === 'no') ? 4 : 0;
        skip += (answers?.reason === 'reason1') ? 2 : 0;
        skip += (answers?.condition === 'no') ? 3 : 0;
    } else if (answers?.product === 'toupee') {
        skip += (answers?.situation === 'no') ? 4 : 0;
        skip += (answers?.condition === 'no') ? 2 : 0;
    }
    /* eslint-enable @nfq/no-magic-numbers */

    return skip;
};